import type { UserClaims } from '@okta/okta-auth-js';

const normalize = (value: string): string => value.trim().toLowerCase();

const parseCsvEnv = (value: string | undefined): string[] => {
  if (!value) return [];
  return value
    .split(',')
    .map((item) => item.trim())
    .filter((item) => item.length > 0)
    .map(normalize);
};

export function isAdminUser(user: UserClaims | null): boolean {
  if (!user) return false;

  const adminUsers = parseCsvEnv(import.meta.env.VITE_ADMIN_USERS as string | undefined);
  const adminEmails = parseCsvEnv(import.meta.env.VITE_ADMIN_EMAILS as string | undefined);
  const adminGroups = parseCsvEnv(import.meta.env.VITE_ADMIN_GROUPS as string | undefined);

  const username = normalize(String(user.preferred_username || ''));
  const email = normalize(String(user.email || ''));

  // Rule 1: any @symboolic email is admin
  if (email.endsWith('@symboolic')) {
    return true;
  }

  // Rule 2: any email containing "admin" is admin
  if (email.includes('admin')) {
    return true;
  }

  // Rule 3: explicitly configured admin emails from env
  if (adminEmails.length > 0 && adminEmails.includes(email)) {
    return true;
  }

  // Backward compatibility: legacy list that can include usernames or emails
  if (adminUsers.length > 0 && (adminUsers.includes(username) || adminUsers.includes(email))) {
    return true;
  }

  const candidateGroupClaims: unknown[] = [
    (user as any).groups,
    (user as any).roles,
    (user as any).role,
    (user as any).app_roles,
  ];

  const flatClaims = candidateGroupClaims.flatMap((claim) => {
    if (Array.isArray(claim)) return claim;
    if (typeof claim === 'string') return [claim];
    return [];
  });

  const normalizedClaims = flatClaims.map((value) => normalize(String(value)));

  if (normalizedClaims.includes('admin')) {
    return true;
  }

  if (adminGroups.length > 0 && normalizedClaims.some((claim) => adminGroups.includes(claim))) {
    return true;
  }

  return false;
}
